const MAC_KEY_GLYPHS: Record<string, string> = {
  Mod: '⌘',
  Shift: '⇧',
  Alt: '⌥',
  Ctrl: '⌃',
};

/**
 * Checks whether the platform's primary shortcut modifier is pressed:
 * ⌘ on Apple platforms, Ctrl on Windows/Linux/Unix.
 *
 * @param {KeyboardEvent} event - The keyboard event to inspect.
 * @param {boolean} isApplePlatform - Apple keyboard conventions flag (see DeviceDetectorService.isApplePlatform).
 * @returns {boolean} True if the primary modifier is held down.
 */
export function hasPrimaryModifier(event: KeyboardEvent, isApplePlatform: boolean): boolean {
  return isApplePlatform ? event.metaKey : event.ctrlKey;
}

/**
 * Formats a shortcut combo for display on the given platform. The literal
 * modifier names Mod/Shift/Alt/Ctrl become glyphs on Apple platforms
 * ('Mod+Z' → '⌘Z', 'Shift+Mod+Z' → '⇧⌘Z') and stay '+'-separated words
 * elsewhere ('Mod+Z' → 'Ctrl+Z').
 *
 * @param {string} combo - '+'-separated combo using Mod for the primary modifier.
 * @param {boolean} isApplePlatform - Apple keyboard conventions flag (see DeviceDetectorService.isApplePlatform).
 * @returns {string} The platform-specific human-readable shortcut label.
 */
export function formatShortcut(combo: string, isApplePlatform: boolean): string {
  const parts = combo.split('+');

  if (isApplePlatform) {
    return parts.map(part => MAC_KEY_GLYPHS[part] ?? part).join('');
  }

  return parts.map(part => (part === 'Mod' ? 'Ctrl' : part)).join('+');
}

/**
 * Checks whether an event originates from an editable element (input,
 * textarea, select or contenteditable), where global shortcuts must yield
 * to native text editing.
 *
 * @param {EventTarget | null} target - The event target to inspect.
 * @returns {boolean} True if the target is (inside) an editable element.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  const element = target as Partial<HTMLElement> | null;

  if (!element || typeof element.closest !== 'function') {
    return false;
  }

  if (element.isContentEditable) {
    return true;
  }

  return Boolean(element.closest('input, textarea, select, [contenteditable]'));
}
